<script setup lang="ts">
/**
 * The contextual assistant's card (Figma `Contextual assistant message`, 320 wide):
 * white, radius 24 with a 2px bottom-right corner, drop shadow 0 0 20 rgba(26,33,51,.1),
 * padding 16 / 24 right, Inter 14/1.5 body, Gilroy 14 chips, × top-right.
 *
 * The card sits right above AIRA's floating ball and belongs to it: the ball is
 * the one speaking. It used to draw a second, static ball in its own corner,
 * which put two AIRAs on screen at once — that copy is gone.
 */
import { useI18n } from 'vue-i18n'
import iconClose from '@/ui/icons/main/close.svg?url'
import { useAiraContextualStore } from './useAiraContextualStore'
import { useAiraContextualCommands } from './useAiraContextualCommands'

const store = useAiraContextualStore()
const { run } = useAiraContextualCommands()
const { t } = useI18n()
</script>

<template>
  <Transition name="fair-aira-ctx">
    <aside
      v-if="store.current"
      :key="store.current.id"
      class="fair-aira-ctx"
      role="status"
      :aria-label="t('aira_contextual.label')"
      data-testid="aira-contextual-card"
    >
      <p
        v-if="store.current.title"
        class="fair-aira-ctx__title"
      >
        {{ store.current.title }}
      </p>
      <p class="fair-aira-ctx__text">
        {{ store.current.text }}
      </p>
      <p
        v-if="store.current.hint"
        class="fair-aira-ctx__hint"
      >
        {{ store.current.hint }}
      </p>
      <div
        v-if="store.current.highlight"
        class="fair-aira-ctx__highlight"
      >
        {{ store.current.highlight.text }}
      </div>
      <div
        v-if="store.current.buttons.length"
        class="fair-aira-ctx__buttons"
      >
        <button
          v-for="(button, index) in store.current.buttons"
          :key="index"
          type="button"
          class="fair-aira-ctx__button"
          @click="run(store.current.id, button)"
        >
          {{ button.label }}
        </button>
      </div>

      <button
        v-if="store.current.dismissible !== false"
        type="button"
        class="fair-aira-ctx__close"
        :aria-label="t('aira_contextual.close')"
        @click="store.dismiss(store.current.id)"
      >
        <img
          :src="iconClose"
          alt=""
          width="16"
          height="16"
        >
      </button>
    </aside>
  </Transition>
</template>

<style scoped>
.fair-aira-ctx {
  position: fixed;
  z-index: 1090;
  right: 24px;
  /* clears AIRA's ball: 24 bottom + 96 ball + 8 gap */
  bottom: 128px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 320px;
  padding: 16px 24px 16px 16px;
  border-radius: 24px 24px 2px 24px;
  background: var(--color-bg-surface);
  filter: drop-shadow(0 0 10px var(--color-line-hairline));
}

.fair-aira-ctx__title,
.fair-aira-ctx__text,
.fair-aira-ctx__hint {
  margin: 0;
  font: var(--type-body);
  letter-spacing: var(--type-body-tracking);
  overflow-wrap: break-word;
}

.fair-aira-ctx__title {
  color: var(--color-text-primary);
}

.fair-aira-ctx__text {
  color: var(--color-text-secondary);
}

.fair-aira-ctx__hint {
  color: var(--color-text-muted);
}

.fair-aira-ctx__highlight {
  padding: 12px;
  border-radius: 8px;
  background: var(--color-fill-chip);
  color: var(--color-text-secondary);
  font: var(--type-body);
  letter-spacing: var(--type-body-tracking);
}

.fair-aira-ctx__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

/* type/button: Gilroy Medium 14, ls .28, in a 22px chip */
.fair-aira-ctx__button {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: var(--color-fill-chip);
  color: var(--color-text-secondary);
  font: var(--type-button);
  letter-spacing: var(--type-button-tracking);
  white-space: nowrap;
  cursor: pointer;
}

.fair-aira-ctx__button:hover {
  background: var(--color-line-hairline);
}

.fair-aira-ctx__close {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
}

.fair-aira-ctx-enter-active,
.fair-aira-ctx-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fair-aira-ctx-enter-from,
.fair-aira-ctx-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 1023px) {
  .fair-aira-ctx {
    right: 16px;
    left: 16px;
    /* above the mobile bottom nav and the ball */
    bottom: 160px;
    width: auto;
  }
}
</style>
